import { CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import HelpTooltip from './HelpTooltip';
import type { AuditReportData } from '../types';
import ScoreCard from './ScoreCard';
import FixSnippets from './FixSnippets';
import SchemaAnalysis from './SchemaAnalysis';

export default function AuditReport({ data }: { data: AuditReportData }) {
  const { score, checks, fix_schema, fix_llms, meta, summary } = data;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pass': return 'text-green-600 bg-green-50 border-green-200';
      case 'Warn': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'Fail': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  const getIcon = (status: string) => {
    switch (status) {
      case 'Pass': return <CheckCircle2 size={20} />;
      case 'Warn': return <AlertTriangle size={20} />;
      case 'Fail': return <XCircle size={20} />;
      default: return null;
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <ScoreCard meta={meta} score={score} />

      {/* NEW: AI Summary Card */}
      {summary && (
        <div className="bg-gradient-to-r from-indigo-50 to-white border border-indigo-100 p-6 rounded-3xl shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10 text-indigo-900 pointer-events-none">
            <CheckCircle2 size={120} />
          </div>
          <h3 className="text-indigo-900 font-bold uppercase tracking-wider text-xs mb-2 flex items-center gap-2">
            <span className="bg-indigo-600 text-white px-2 py-0.5 rounded text-[10px]">AI VERDICT</span>
            Executive Summary
          </h3>
          <p className="text-indigo-900/80 text-lg leading-relaxed font-medium relative z-10">
            {summary.split("**").map((part, i) =>
              i % 2 === 1 ? <span key={i} className="text-indigo-700 font-black bg-indigo-50/50 px-1 rounded">{part}</span> : part
            )}
          </p>
        </div>
      )}

      <SchemaAnalysis meta={meta} />

      <div className="grid md:grid-cols-2 gap-4">
        {Object.entries(checks).map(([key, val]) => {
          // Content Strategy: Map technical keys to "Plain English" + Tooltips
          let friendlyName = key;
          let tooltipData = { title: key, desc: "", benefit: "" };

          switch (key) {
            case "Bot Access":
              friendlyName = "Bot Access";
              tooltipData = { title: "Bot Access (robots.txt)", desc: "Is your digital front door open for AI visitors?", benefit: "Ensures AI crawlers can actually read your site." };
              break;
            case "llms.txt":
              friendlyName = "AI Sitemap";
              tooltipData = { title: "AI Sitemap (llms.txt)", desc: "A special map that tells ChatGPT exactly which pages to read.", benefit: "Gives you control over what AI learns about you." };
              break;
            case "Entity Identity":
              friendlyName = "Brand Detection";
              tooltipData = { title: "Brand Detection (Schema)", desc: "Can AI bots clearly identify your brand name and logo?", benefit: "Prevents AI from hallucinating details about your company." };
              break;
            case "Trust Triangle":
              friendlyName = "Identity Verification";
              tooltipData = { title: "Identity Verification (Trust Triangle)", desc: "Proves that your website, LinkedIn, and Crunchbase are all the same company.", benefit: "The highest signal of trust for Google & AI." };
              break;
            case "Disambiguation":
              friendlyName = "Brand Distinctiveness";
              tooltipData = { title: "Brand Distinctiveness", desc: "Prevents AI from confusing you with other companies that have similar names.", benefit: "Essential if you have a common business name." };
              break;
            case "Entity Graph":
              friendlyName = "Knowledge Graph";
              tooltipData = { title: "Knowledge Graph Density", desc: "Are you mentioning other clear entities (People, Places, Organizations)?", benefit: "Helps AI connect your brand to the wider world." };
              break;
            case "Q&A Structure":
              friendlyName = "Answer Format";
              tooltipData = { title: "Answer Format", desc: "Are your headings written as questions that users ask?", benefit: "Increases chance of being featured in AI answers." };
              break;
            case "Information Gain":
              friendlyName = "Data Depth";
              tooltipData = { title: "Data Depth", desc: "How much unique data, numbers, and facts you provide compared to fluff.", benefit: "AI prioritizes content with high information density." };
              break;
            case "H1 Tag":
              friendlyName = "Main Topic (H1)";
              tooltipData = { title: "Main Topic Presence", desc: "Does the page have a clear main headline?", benefit: "Fundamental for both SEO and AI understanding." };
              break;
          }

          return (
            <div key={key} className={`p-5 rounded-2xl border flex items-center justify-between ${getStatusColor(val.status)} transition-all hover:shadow-md`}>
              <div className="flex items-center gap-3">
                {getIcon(val.status)}
                <div>
                  <h3 className="font-bold flex items-center">
                    {friendlyName}
                    <HelpTooltip title={tooltipData.title} description={tooltipData.desc} benefit={tooltipData.benefit} />
                  </h3>
                  <p className="text-xs opacity-80 mt-0.5">{val.detail}</p>
                </div>
              </div>
              <div className="text-xs font-bold px-2 py-1 rounded-full bg-white/50 backdrop-blur-sm uppercase">
                {val.status}
              </div>
            </div>
          );
        })}
      </div>

      {/* NEW: Content & Sentiment Analysis Section */}
      {data.content_strategy && (
        <div className="bg-slate-100 rounded-3xl p-8 border border-slate-200">
          <h3 className="text-slate-500 font-medium uppercase tracking-wider text-sm mb-6">Content Quality Analysis</h3>
          <div className="grid md:grid-cols-2 gap-6">

            {/* Sentiment Card */}
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                Brand Tone / Sentiment
                <HelpTooltip
                  title="Brand Tone"
                  description="The emotional quality of your content (Positive, Neutral, Negative)."
                  benefit="Positive content is more likely to be cited in recommendation scenarios."
                />
              </h4>
              <div className="flex items-end gap-3 mb-2">
                <span className="text-4xl font-black text-indigo-600">{data.content_strategy.sentiment?.score || 0}</span>
                <span className="text-slate-400 font-medium pb-1">/ 100</span>
              </div>
              <div className="text-sm px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 inline-block font-bold">
                {data.content_strategy.sentiment?.label || 'Neutral'}
              </div>
            </div>

            {/* Hallucination Risk Card */}
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                Hallucination Risk
                <HelpTooltip
                  title="Hallucination Risk"
                  description="A score predicting how likely AI is to make up false information due to vague content."
                  benefit="Lower risk means more accurate AI answers about your brand."
                />
              </h4>
              <div className="flex items-end gap-3 mb-2">
                <span className={`text-4xl font-black ${data.content_strategy.hallucination_risk?.score < 50 ? 'text-red-500' : 'text-green-500'}`}>
                  {data.content_strategy.hallucination_risk?.score || 100}
                </span>
                <span className="text-slate-400 font-medium pb-1">/ 100</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {data.content_strategy.hallucination_risk?.triggers?.length > 0
                  ? `Triggers: ${data.content_strategy.hallucination_risk.triggers.join(", ")}`
                  : "✅ Low Risk: content is specific and data-driven."}
              </p>
            </div>

          </div>
        </div>
      )}

      <FixSnippets fix_llms={fix_llms} fix_schema={fix_schema} />
    </div>
  );
}